// ✅ routes/accountPage/subpages/AccountBookings.jsx
import "./accountBookings.css";
import { useState, useEffect, useContext } from "react";
import usersRequest from "../../../request/usersRequest";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import { useNavigate } from "react-router-dom";
import generalRequest from "../../../request/generalRequest";
import BookingModal from "../../bookingModal/bookingModal";

const AccountBookings = () => {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [rescheduleData, setRescheduleData] = useState(null);
    const [shopData, setShopData] = useState(null);


	const { userData, language } = useContext(CombinedContext);
	const navigate = useNavigate();

	const isLoggedIn = !!(userData && userData.cognitoTokens?.IdToken);
	const userId = userData?.user?.userId;

	useEffect(() => {
		async function loadBookings() {
			if (!isLoggedIn || !userId) {
				setLoading(false);
				return;
			}
			try {
				const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
				setBookings(data.bookings || []);
			} catch (err) {
				setError(err.message || "Could not load bookings");
			} finally {
				setLoading(false);
			}
		}
		loadBookings();
	}, [isLoggedIn, userId, userData?.cognitoTokens?.IdToken]);

	const now = new Date();


	const upcoming = bookings.filter(
	  (b) =>
		new Date(b.startTime) > now &&
		!["cancelled", "no_show"].includes(b.status)
	);
	
	const history = bookings.filter(
	  (b) =>
		new Date(b.startTime) <= now ||
		["cancelled", "no_show"].includes(b.status)
	);
	

	const handleCardClick = (bookingId) => {
		navigate(`/booking/${bookingId}`);
	};

	async function handleCancel(bookingId) {
		try {
			await scheduleBasedRequest.cancelBooking(bookingId, userData.cognitoTokens?.IdToken);
			setBookings((prev) => prev.filter((b) => b.bookingId !== bookingId));
		} catch (err) {
			setError(err.message || "Could not cancel booking");
		}
	}

	const reloadBookings = async () => {
        try {
            const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
            setBookings(data.bookings || []);
        } catch (err) {
            setError(err.message || "Could not reload bookings");
        }
    };

    async function handleReschedule(booking) {
        try {
            const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);

            setShopData(res.shop);
            setRescheduleData(booking);
            setIsBookingOpen(true);
        } catch (err) {
            setError(err.message || "Unable to reschedule booking");
        }
    }

	return (
		<div className="sf-account-bookings">
			<h2 className="sf-account-title">{translations[language].myBookings.upcomingTitle}</h2>

			{loading && <div>{translations[language].myBookings.upcomingSubLoading}</div>}
			{error && <div style={{ color: "red" }}>{error}</div>}

			<div className="sf-booking-list">
				{!loading && upcoming.length === 0 && (
					<div className="sf-empty">{translations[language].myBookings.noUpcoming}</div>
				)}

				{upcoming.map((b) => (
					<div
						key={b.bookingId}
						className="sf-booking-card-item clickable"
						onClick={() => handleCardClick(b.bookingId)}
					>
						<div className="sf-booking-info">
							<div className="sf-booking-shop">{b.shopName}</div>
							<div className="sf-booking-details">
								{new Date(b.startTime).toLocaleDateString(language, {
									weekday: "short",
									month: "short",
									day: "numeric",
								})}{" "}
								•{" "}
								{new Date(b.startTime).toLocaleTimeString([], {
									hour: "2-digit",
									minute: "2-digit",
								})}
							</div>
						</div>

						<div className="sf-booking-actions">
							<button
									className="sf-btn-secondary"
									onClick={(e) => {
										e.stopPropagation();
										handleReschedule(b);
									}}
								>

									{translations[language].myBookings.reschedule}
							</button>
							<button
								className="sf-btn-danger"
								onClick={(e) => {
									e.stopPropagation();
									handleCancel(b.bookingId);
								}}
							>
								{translations[language].myBookings.cancel}
							</button>
						</div>
					</div>
				))}
			</div>

			<h2 className="sf-account-title" style={{ marginTop: "2rem" }}>
				{translations[language].myBookings.historyTitle}
			</h2>

			<div className="sf-booking-history">
				{history.length === 0 ? (
					<div className="sf-empty">{translations[language].myBookings.noHistory}</div>
				) : (
					<table>
						<thead>
							<tr>
								<th>{translations[language].myBookings.table.date}</th>
								<th>{translations[language].myBookings.table.service}</th>
								<th>{translations[language].myBookings.table.shop}</th>
								<th>{translations[language].myBookings.table.status}</th>
							</tr>
						</thead>
						<tbody>
							{history.map((h) => (
								<tr
									key={h.bookingId}
									className="clickable"
									onClick={() => handleCardClick(h.bookingId)}
								>
									<td>{new Date(h.startTime).toLocaleDateString(language, { month: "short", day: "numeric" })}</td>
									<td>{h.serviceNameEn}</td>
									<td>{h.shopName || "—"}</td>
									<td>{h.status}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
			{isBookingOpen && shopData && rescheduleData && (
                <BookingModal
                    open={isBookingOpen}
                    onClose={() => {
                        setIsBookingOpen(false);
                        setRescheduleData(null);
                        setShopData(null);
                        reloadBookings();
                    }}
                    availabilityId={shopData.shopId}
                    shopData={shopData}
                    initialServiceId={rescheduleData.services?.[0]?.serviceId}
                    initialstaffId={rescheduleData.staffId}
                    mode="reschedule"
                    existingBooking={rescheduleData}
                />
            )}
		</div>
	);
};

export default AccountBookings;
